import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Package } from "lucide-react";
import { toast } from "sonner";

/**
 * FEFO Picker Popover — Iter-103.
 * Reusable inline picker that calls /reports/fefo-picking for the given
 * item + warehouse + qty and lists the suggested batches (First-Expiry-First-Out).
 */
export default function FEFOPickerPopover({ itemId, warehouseId = null, qty = 1, onPick, buttonSize = "sm" }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!itemId) return toast.error("Pick an item first");
    setLoading(true);
    try {
      const params = { item_id: itemId, qty };
      if (warehouseId) params.warehouse_id = warehouseId;
      const r = await api.get("/reports/fefo-picking", { params });
      setD(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  return (
    <Popover onOpenChange={(o) => o && !d && load()}>
      <PopoverTrigger asChild>
        <Button size={buttonSize} variant="outline" className="rounded-sm h-7 gap-1 text-xs" data-testid="fefo-picker-trigger">
          <Package className="w-3 h-3" /> FEFO
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 rounded-sm">
        {loading && <div className="text-xs text-muted-foreground">Loading…</div>}
        {d && (
          <div className="space-y-2" data-testid="fefo-picker-content">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">// FEFO picks · need {qty}</div>
            {d.picks.length === 0 && <div className="text-xs text-muted-foreground">No expiry-dated batches available.</div>}
            {d.picks.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-xs border-b pb-1 last:border-0" data-testid={`fefo-pick-${i}`}>
                <div>
                  <div className="font-mono">{p.batch_no || "no-batch"}</div>
                  <div className="text-[10px] text-muted-foreground">exp {p.exp_date} · avail {p.available}</div>
                </div>
                <div className="text-right">
                  <div className="tabular-nums font-medium">{p.suggested_qty}</div>
                  <button onClick={() => onPick?.(p)} className="text-[10px] font-mono text-accent hover:underline">Use →</button>
                </div>
              </div>
            ))}
            {d.shortfall > 0 && <div className="text-[10px] text-red-700 font-mono">shortfall {d.shortfall}</div>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
